/*
 * Category layouts: how the Actions of one category are drawn. The category type decides the layout; the icon does not.
 * Every layout keeps the same behaviour as the original grid:
 *  - a link with a URL is a real <a> (opens in a new tab); a memo or a link without URL is a button (read / add link);
 *  - share is a separate button next to the link, never inside it, so tapping share cannot open the link;
 *  - in edit mode every item is a button that opens the editor (edit, delete);
 *  - long press (data-press) opens the details sheet.
 * Cards only show data the app really has: icon, colour, name, the link's hostname, memo text. There is no image yet
 * (no metadata fetch or upload); imageOf() is where a real cover will come from later, and every card already has a
 * placeholder of the same size, so a picture can drop in without changing the layout.
 */
import { esc } from "../domain/format";
import { CATEGORY_LAYOUTS, type Action, type CategoryLayout } from "../domain/types";
import { col, glassIcon, ic } from "./icons";

export const layoutName = (k: CategoryLayout) => CATEGORY_LAYOUTS.find(l => l.key === k)?.name ?? k;

/** hostname without "www.", or "" */
export function hostOf(url: string): string {
  if (!url) return "";
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return ""; }
}
/** a real cover image for this Action; none yet (metadata / upload is a later task) */
const imageOf = (_a: Action): string | null => null;

const glass = (a: Action, size?: number) => {
  const g = glassIcon(a.icon, col(a.color));
  return `<span class="iconwrap">${size ? g.replace('class="gi"', `class="gi" style="width:${size}px;height:${size}px"`) : g}${a.type === "memo" ? `<span class="memo-dot">${ic("lines")}</span>` : ""}</span>`;
};
/** second line under the name: hostname, first line of the memo, or "no link yet" */
function detail(a: Action): { text: string; warn: boolean } {
  if (a.type === "link") { const h = hostOf(a.url); return h ? { text: h, warn: false } : { text: "ยังไม่มีลิงก์", warn: true }; }
  const first = a.text.split("\n").map(s => s.trim()).find(Boolean);
  return { text: first || "Memo", warn: false };
}
const sub = (a: Action, cls = "sub") => { const d = detail(a); return `<span class="${cls}${d.warn ? " warn" : ""}">${esc(d.text)}</span>`; };

/** cover: real image when there is one, otherwise a tinted placeholder with the icon (and the hostname when asked; the cards here show it under the name instead) */
function cover(a: Action, withHost: boolean) {
  const img = imageOf(a);
  if (img) return `<span class="cover"><img src="${esc(img)}" alt="" loading="lazy"></span>`;
  const h = withHost && a.type === "link" ? hostOf(a.url) : "";
  return `<span class="cover ph" style="--c:${col(a.color)}">${glass(a, 44)}${h ? `<span class="cover-host">${esc(h)}</span>` : ""}</span>`;
}

interface Opts { edit: boolean; pen: string }
function item(a: Action, o: Opts, cls: string, inner: string) {
  const opens = !o.edit && a.type === "link" && a.url;
  const main = opens
    ? `<a class="cell act ${cls}" href="${esc(a.url)}" target="_blank" rel="noopener noreferrer" data-do="open-link" data-id="${a.id}" data-press="act">${o.pen}${inner}</a>`
    : `<button class="cell act ${cls}" data-do="act" data-id="${a.id}" data-press="act">${o.pen}${inner}</button>`;
  const share = !o.edit && a.type === "link" ? `<button class="share ${a.url ? "" : "none"}" data-do="share" data-id="${a.id}" aria-label="แชร์ ${esc(a.name)}">${ic("share")}</button>` : "";
  return `<div class="actcell">${main}${share}</div>`;
}
const name = (a: Action) => `<span class="name">${esc(a.name)}</span>`;

const ITEMS: Record<Exclude<CategoryLayout, "standard">, (a: Action, o: Opts) => string> = {
  work: (a, o) => item(a, o, "lrow", `${glass(a, 40)}<span class="lt">${name(a)}${sub(a)}</span>`),
  links: (a, o) => item(a, o, "lpill", `${glass(a, 30)}${name(a)}`),
  places: (a, o) => item(a, o, "lcard", `${glass(a, 48)}${name(a)}${sub(a)}`),
  lifestyle: (a, o) => item(a, o, "lsq", `<span class="sq-ic" style="--c:${col(a.color)}">${glass(a, 52)}</span>${name(a)}`),
  media: (a, o) => item(a, o, "lmedia", `${cover(a, false)}<span class="lt">${name(a)}${sub(a)}</span>`),
  shop: (a, o) => item(a, o, "lshop", `${cover(a, false)}<span class="lt">${name(a)}${sub(a)}</span>`),
  inspiration: (a, o) => item(a, o, "lmood", imageOf(a)
    ? `${cover(a, false)}${name(a)}`
    : `${glass(a, 40)}${name(a)}${a.type === "memo" && a.text ? `<span class="mood-text">${esc(a.text.slice(0, 160))}</span>` : sub(a)}`)
};

/** the Actions area of a non-standard category: items, the empty note and the add button */
export function layoutHTML(layout: Exclude<CategoryLayout, "standard">, acts: Action[], o: Opts, empty: string, withAdd = true) {
  const add = withAdd ? `<button class="cell act addc" data-do="new-act" id="btn-add-act"><span class="add-glass">${ic("plus")}</span><span class="name addl">เพิ่ม Action</span></button>` : "";
  return `<div class="lay lay-${layout}" data-layout="${layout}">${empty}${acts.map(a => ITEMS[layout](a, o)).join("")}${add}</div>`;
}

/**
 * Sample Actions for the live preview in the category editor. They exist only in the preview (never saved), carry the
 * colour being picked, and use icons from the icon group that matches the category type.
 */
const SAMPLES: Record<CategoryLayout, [string, string, "link" | "memo"][]> = {
  work: [["Sheet งบประมาณ", "sheet", "link"], ["ปฏิทินทีม", "calendar", "link"], ["บันทึกประชุม", "note", "memo"]],
  links: [["เว็บไซต์", "globe", "link"], ["อีเมล", "mail", "link"], ["แชททีม", "chat", "link"], ["โทรศัพท์", "phone", "memo"]],
  places: [["ร้านกาแฟ", "pin", "link"], ["ออฟฟิศ", "office", "link"], ["ผู้ติดต่อ", "user", "memo"]],
  lifestyle: [["สูตรอาหาร", "food", "link"], ["ออกกำลังกาย", "fitness", "link"], ["ทริปเที่ยว", "travel", "link"], ["สุขภาพ", "heart", "memo"]],
  media: [["วิดีโอ", "video", "link"], ["เพลย์ลิสต์", "music", "link"]],
  shop: [["ร้านค้า", "store", "link"], ["รายการซื้อ", "cart", "memo"], ["ของขวัญ", "gift", "link"]],
  inspiration: [["ไอเดีย", "bulb", "memo"], ["งานออกแบบ", "design", "link"], ["ภาพถ่าย", "camera", "link"]],
  standard: [["ลิงก์", "link", "link"], ["เว็บไซต์", "globe", "link"], ["โน้ต", "note", "memo"], ["ดาว", "star", "link"]]
};
export function sampleActions(layout: CategoryLayout, color: string): Action[] {
  return SAMPLES[layout].map(([name, icon, type], i) => ({
    id: `sample-preview-${i}`, categoryId: "sample-preview", type, name, icon, color, position: i, sample: true,
    url: type === "link" ? "https://example.com" : "", text: type === "memo" ? "ข้อความตัวอย่าง" : "", createdAt: "", updatedAt: ""
  }));
}

/* small schematic of each layout for the category type picker (56×40) */
const r = (x: number, y: number, w: number, h: number, rx = 2, o = 1) => `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${rx}" opacity="${o}"/>`;
const THUMB: Record<CategoryLayout, string> = {
  work: [4, 15, 26].map(y => r(4, y, 9, 9) + r(16, y + 1, 30, 3, 1.5) + r(16, y + 6, 20, 2, 1, .5)).join(""),
  links: [4, 21].map(y => r(4, y, 23, 14, 5) + r(30, y, 23, 14, 5)).join(""),
  places: r(4, 4, 23, 32, 4, .55) + r(30, 4, 23, 32, 4, .55) + r(8, 8, 9, 9, 3) + r(34, 8, 9, 9, 3) + r(8, 22, 14, 3, 1.5) + r(34, 22, 14, 3, 1.5),
  lifestyle: [4, 21].map(y => r(10, y, 15, 15, 4) + r(31, y, 15, 15, 4)).join(""),
  media: r(4, 4, 48, 24, 4, .55) + r(23, 11, 10, 10, 3) + r(4, 31, 30, 3, 1.5) + r(4, 36, 18, 2, 1, .5),
  shop: r(4, 4, 23, 20, 4, .55) + r(30, 4, 23, 20, 4, .55) + r(4, 27, 18, 3, 1.5) + r(30, 27, 18, 3, 1.5) + r(4, 33, 12, 2, 1, .5) + r(30, 33, 12, 2, 1, .5),
  inspiration: r(4, 4, 23, 20, 4, .55) + r(4, 27, 23, 10, 4, .85) + r(30, 4, 23, 11, 4, .85) + r(30, 18, 23, 19, 4, .55),
  standard: [6, 23].map(y => [6, 22, 38].map(x => r(x, y, 12, 12, 3)).join("")).join("")
};
export const layoutThumb = (k: CategoryLayout) => `<svg class="lthumb" viewBox="0 0 56 40" aria-hidden="true"><g fill="currentColor">${THUMB[k]}</g></svg>`;
